//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import { BodyText, UiCopyText } from '@components/atoms/text/Text';
import { HarmonyAIIcon } from '@components/harmonyai/HarmonyAIIcon';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { HashtagDropdownTitleLabel } from '@components/molecules/smart-input/recommended-hashtags/HashtagDropdownTitleLabel';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import { getSmartInputTrackingId } from '@components/molecules/smart-input/smartInputUtils';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { ToggleButtonGroup } from '@components/primitives/toggle-button-group/ToggleButtonGroup';

const enum HashtagToggleOption {
  Recent = 'Most Used by You',
  RecommendedAi = 'Recommended',
}

interface Props {
  disabledText?: string;
  disabledTierTooltipContent?: React.ReactNode;
  isRecentHashtagsSelected: boolean;
  isRecommendedAiHashtagsEnabled: boolean;
  isRecommendedAiHashtagsEnabledForPlanTier: boolean;
  setIsRecentHashtagsSelected: (isMostUsedSelected: boolean) => void;
  shouldDisplayToggle: boolean;
}

export const HashtagDropdownTitle = ({
  disabledText,
  disabledTierTooltipContent,
  isRecentHashtagsSelected,
  isRecommendedAiHashtagsEnabled,
  isRecommendedAiHashtagsEnabledForPlanTier,
  setIsRecentHashtagsSelected,
  shouldDisplayToggle,
}: Props) => {
  const { trackingIdPrefix } = useSmartInputContext();

  if (!isRecommendedAiHashtagsEnabled) {
    return (
      <>
        <HarmonyAIIcon />
        <TitleText>Recommended Hashtags</TitleText>
        <HelpIcon
          appendTarget='body'
          helpText='Hashtag recommendations are based on your most commonly used hashtags.'
          placement='right'
        />
      </>
    );
  }

  return shouldDisplayToggle ? (
    <ToggleButtonGroup
      isDisabled={!isRecommendedAiHashtagsEnabledForPlanTier || !!disabledText}
      onValueChange={(value: HashtagToggleOption) => {
        switch (value) {
          case HashtagToggleOption.Recent:
            setIsRecentHashtagsSelected(true);
            break;
          case HashtagToggleOption.RecommendedAi:
            setIsRecentHashtagsSelected(false);
            break;
          default:
            break;
        }
      }}
    >
      <ToggleButtonGroup.Option
        isSelected={isRecentHashtagsSelected}
        trackingId={getSmartInputTrackingId(
          trackingIdPrefix,
          SmartInputTrackingId.ShowRecommendedRecentHashtags,
        )}
        value={HashtagToggleOption.Recent}
      >
        {HashtagToggleOption.Recent}
      </ToggleButtonGroup.Option>
      <ToggleButtonGroup.Option
        isSelected={!isRecentHashtagsSelected}
        trackingId={getSmartInputTrackingId(
          trackingIdPrefix,
          SmartInputTrackingId.ShowRecommendedAiHashtags,
        )}
        value={HashtagToggleOption.RecommendedAi}
      >
        {!isRecommendedAiHashtagsEnabledForPlanTier || disabledText ? (
          <SmallTooltip
            content={
              <BodyText>
                {!isRecommendedAiHashtagsEnabledForPlanTier ? (
                  <>
                    Upgrade to unlock AI hashtag recommendations.&nbsp;
                    {disabledTierTooltipContent ??
                      'Contact Bazaarvoice Support to learn more.'}
                  </>
                ) : (
                  disabledText
                )}
              </BodyText>
            }
            target={
              <>{<HashtagDropdownTitleLabel shouldUsePrimaryColor={false} />}</>
            }
          />
        ) : (
          <HashtagDropdownTitleLabel
            shouldUsePrimaryColor={!isRecentHashtagsSelected}
          />
        )}
      </ToggleButtonGroup.Option>
    </ToggleButtonGroup>
  ) : (
    <HashtagDropdownTitleLabel shouldUsePrimaryColor={true} />
  );
};

const TitleText = styled(UiCopyText)(
  ({ theme }) => `
  display: inline-block;
  margin: 0 ${theme.spacings[2]};
`,
);
